import {
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  Prop,
  Watch,
} from '@stencil/core';

import { FOOTER_ENTRY } from '../../constants/constants';
import { ElementRegistry } from '../../utils/elements/element-registry.utils';
import { Utils } from '../../utils/utils';

/**
 * @displayName Footer
 *
 * @contextData FLUID Footer provides a consistent way to display important information and key navigation links at the bottom of every page in an application. Footer should be implemented as a sibling (NOT a child) of FLUID Page.
 * <br/><br/>
 * <strong>NOTE: please DO NOT use Footer's "sticky" Prop for best user experience. If the Prop was previously used as a workaround to ensure that Footer appears at the bottom of the page, please remove it - the underlying issue that caused Footer to be displayed above the bottom of the viewport has been fixed.</strong>
 *
 * @slot footer-actions - Named slot for site logo, navigation links, etc. that appears above "footer-content" slot. Can be used with or without FLUID Footer Actions component.
 * @slot footer-content - Named slot for copyright, legal information, disclaimer, etc. that appears below "footer-actions" slot. Can be used with or without FLUID Footer Content component.
 */
@Component({
  tag: 'fluid-footer',
  styleUrl: 'fluid-footer.less',
  shadow: true,
})
export class FluidFooter implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Reference to root <footer> element.
   */
  private _containerRef: HTMLElement;

  /**
   * Reference to Footer's placeholder element.
   */
  private _placeholderRef: HTMLElement;

  /**
   * Reference to ResizeObserver instance that monitors Footer's height
   * for changes. Used for triggering Element Registry updates (for FLUID
   * Page's "min-height" calculation) & to set "sticky" placeholder height.
   */
  private _resizeObserver: ResizeObserver;

  /**
   * Number that tracks the <footer> element's height.
   */
  private _containerHeight: number;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidFooterElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action setGlobalTheme - updates theme to match global theme & subscribes to changes.
   * @action setZIndex - Set component z-index relative to global base value
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action stickyDeprecationWarning - "sticky" Prop deprecation warning & placeholder height update
   */
  componentWillLoad() {
    this.stickyDeprecationWarning();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action updateFooterHeight - triggers update to Element Registry & placeholder height
   * @action initializeResizeObserver - begins monitoring Footer height for changes
   * @action addElement - adds Footer to global Element Registry
   */
  componentDidLoad() {
    this.updateFooterHeight();
    this.initializeResizeObserver();
    ElementRegistry.addElement(FOOTER_ENTRY, this.host);
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action removeElement - removes Footer from global Element Registry
   */
  disconnectedCallback() {
    ElementRegistry.removeElement(FOOTER_ENTRY, this.host);

    this._resizeObserver?.disconnect();
    delete this._resizeObserver;

    delete this._containerRef;
    delete this._placeholderRef;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Initializes ResizeObserver that watches for changes in the size of
   * Footer's main container (due to changes in viewport size, theme,
   * Prop values, etc.). More reliable/accurate & less resource-intensive
   * than "window.resize" listener.
   */
  initializeResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') return;

    this._resizeObserver = new ResizeObserver((entries) => {
      const containerHeight = entries[0]?.borderBoxSize[0]?.blockSize;
      if (!!containerHeight && containerHeight !== this._containerHeight) {
        this.updateFooterHeight(containerHeight);
      }
    });
    this._resizeObserver.observe(this._containerRef);
  }

  /**
   * Updates internal variable that tracks Footer height, triggers update
   * to Footer placeholder height (if "sticky" enabled), & triggers
   * update to Element Registry (for FLUID Page's "min-height" calculation).
   */
  updateFooterHeight(
    containerHeight: number = this._containerRef?.offsetHeight
  ) {
    if (!this._containerRef || this._containerHeight === containerHeight)
      return;

    this._containerHeight = containerHeight;
    this.setPlaceholderHeight();
    ElementRegistry.updateRegistry();
  }

  /**
   * Updates Footer placeholder element's height to mirror Footer's height.
   */
  setPlaceholderHeight(): void {
    if (!this.sticky || !this._placeholderRef) return;
    this._placeholderRef.style.height = `${this._containerHeight}px`;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host>
        <footer
          ref={(el) => (this._containerRef = el as HTMLElement)}
          style={this.sticky && { 'z-index': `${this.zIndex}` }}
          class={{
            [`fluid-footer ${this.theme}`]: true,
            'footer-fixed': this.sticky,
          }}>
          <slot name='footer-actions' />
          <slot name='footer-content' />
        </footer>
        {this.sticky && (
          <div
            ref={(el) => (this._placeholderRef = el as HTMLElement)}
            style={{ height: `${this._containerHeight}px` }}
            class='footer-placeholder'
          />
        )}
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index value for sticky Footer.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "sticky" is now deprecated. In general, website footers
   * should NOT have a "fixed" position within the viewport. If this Prop
   * was previously used as a workaround to ensure that Footer appears at
   * the bottom of the page, please remove it - the underlying issue that
   * caused Footer to be displayed above the bottom of the viewport has
   * been fixed. -- Is the footer sticky / fixed?
   * @deprecated
   */
  @Prop({ mutable: true }) sticky: boolean;

  @Watch('sticky')
  stickyDeprecationWarning() {
    if (isNullOrUndefined(this.sticky)) return;
    Utils.consoleWarn(
      this.host,
      'DEPRECATED: Prop "sticky" is now deprecated.\n\nIn general, website footers should NOT have a "fixed" position within the viewport. If this Prop was previously used as a workaround to ensure that Footer appears at the bottom of the page, please remove it - the underlying issue that caused Footer to be displayed above the bottom of the viewport has been fixed.',
      true
    );
    this.updateFooterHeight();
  }
}
